/**
 * PhotoPublishModal — the human-in-the-loop gate before making a photo public.
 *
 * Flow:
 *   1. Caller triggers publish-preview mutation → passes photoId + vehicleId.
 *   2. Modal polls derivativeStatus (via refetchUntilReady) until "ready" or "failed".
 *   3. Shows the REDACTED derivative bytes (GET /preview) — exactly what the
 *      public will see — with a plate/location advisory.
 *   4. Owner clicks "Confirm — make public" → PATCH public_confirmed=true.
 *   5. Cancel → no publish; modal closes.
 *   6. If derivative "failed" → shows error, no confirm button.
 *
 * The preview frame is given role="img" with alt text so screen readers
 * understand what they're reviewing before confirming.
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import { useConfirmPublish } from '@/hooks/usePhotoActions';
import { usePreviewImage } from '@/hooks/usePreviewImage';
import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';

export type DerivativeStatus = 'rendering' | 'ready' | 'failed';

export interface PhotoPublishModalProps {
  photoId: string;
  vehicleId: string;
  derivativeStatus: DerivativeStatus;
  onClose: () => void;
  /** Callback when publish is confirmed — parent should refresh photo list. */
  onConfirmed: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.88)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: SPACE.md,
};

const dialogStyle: CSSProperties = {
  background: T.bg,
  borderRadius: RADIUS.lg,
  width: '100%',
  maxWidth: 560,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${SPACE.md}px ${SPACE.md}px ${SPACE.sm}px`,
  borderBottom: `1px solid ${T.border}`,
  flexShrink: 0,
};

const closeBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: T.text,
  borderRadius: RADIUS.md,
  padding: SPACE.xs,
  minWidth: 44,
  minHeight: 44,
  transition: `background ${TRANSITION.fast}`,
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: SPACE.md,
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.md,
  minHeight: 0,
};

const previewFrameStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '4 / 3',
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
};

const advisoryStyle: CSSProperties = {
  background: T.cardAlt,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  fontSize: 13,
  fontFamily: F.body,
  color: T.text,
  lineHeight: 1.55,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
  padding: `${SPACE.sm}px ${SPACE.md}px ${SPACE.md}px`,
  borderTop: `1px solid ${T.border}`,
  flexShrink: 0,
  justifyContent: 'flex-end',
};

function PreviewArea({
  photoId,
  derivativeStatus,
}: {
  photoId: string;
  derivativeStatus: DerivativeStatus;
}) {
  const previewEnabled = derivativeStatus === 'ready';
  const imgState = usePreviewImage(photoId, previewEnabled);

  if (derivativeStatus === 'rendering') {
    return (
      <div style={previewFrameStyle}>
        <p
          role="status"
          aria-live="polite"
          style={{
            fontSize: 13,
            fontFamily: F.body,
            color: T.textMuted,
            textAlign: 'center',
            margin: 0,
          }}
        >
          Preparing your redacted preview. This takes a few seconds.
        </p>
      </div>
    );
  }

  if (derivativeStatus === 'failed') {
    return (
      <div style={previewFrameStyle}>
        <p
          role="alert"
          style={{ fontSize: 13, fontFamily: F.body, color: T.red, textAlign: 'center', margin: 0 }}
        >
          We couldn't prepare this photo for sharing. It won't be published.
        </p>
      </div>
    );
  }

  // derivativeStatus === 'ready'
  if (imgState.status === 'loading' || imgState.status === 'idle') {
    return (
      <div style={previewFrameStyle}>
        <p
          role="status"
          aria-label="Loading preview"
          style={{ fontSize: 13, fontFamily: F.body, color: T.textMuted, margin: 0 }}
        >
          Loading preview…
        </p>
      </div>
    );
  }

  if (imgState.status === 'ready') {
    return (
      <div style={previewFrameStyle}>
        <img
          src={imgState.url}
          alt="Redacted preview. Faces are blurred automatically, but license plates and other details are not. This is exactly what anyone with the link will see."
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
    );
  }

  // not_found or error — derivative not yet available
  return (
    <div style={previewFrameStyle}>
      <p
        role="alert"
        style={{ fontSize: 13, fontFamily: F.body, color: T.red, textAlign: 'center', margin: 0 }}
      >
        Preview unavailable.
      </p>
    </div>
  );
}

export function PhotoPublishModal({
  photoId,
  vehicleId,
  derivativeStatus,
  onClose,
  onConfirmed,
}: PhotoPublishModalProps) {
  const { dialogRef, initialRef: closeRef } = useModalFocus<HTMLDivElement, HTMLButtonElement>(
    true,
  );
  const confirmMutation = useConfirmPublish(vehicleId);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canConfirm = derivativeStatus === 'ready' && !confirmMutation.isPending;

  function handleConfirm() {
    confirmMutation.mutate({ photoId, confirmed: true }, { onSuccess: onConfirmed });
  }

  return (
    <div style={overlayStyle} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Review before publishing"
        style={dialogStyle}
      >
        <div style={headerStyle}>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: F.body,
              color: T.text,
            }}
          >
            Review before publishing
          </p>
          <button
            ref={closeRef}
            type="button"
            aria-label="Cancel publish"
            style={closeBtnStyle}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div style={bodyStyle}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontFamily: F.body,
              color: T.text,
              lineHeight: 1.6,
            }}
          >
            This is exactly what anyone with the link will see. Only faces are blurred
            automatically. Review carefully before confirming.
          </p>

          <PreviewArea photoId={photoId} derivativeStatus={derivativeStatus} />

          {derivativeStatus !== 'failed' ? (
            <div style={advisoryStyle} role="note">
              <strong>Before you confirm:</strong> license plates are not blurred, so check every
              photo for a visible plate. The face blur can also miss one. Nothing in the background
              is blurred either: street signs, house numbers, anything that shows where the car is
              kept. If anything worries you, cancel and upload a cropped version.
            </div>
          ) : null}

          {confirmMutation.isError ? (
            <p role="alert" style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.red }}>
              Something went wrong. Try again.
            </p>
          ) : null}
        </div>

        <div style={footerStyle}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={confirmMutation.isPending}
          >
            Cancel
          </Button>
          {derivativeStatus !== 'failed' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={!canConfirm}
              loading={confirmMutation.isPending}
            >
              Make public
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
